import {AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {
    AssistanceApplicationClient,
    AssistanceApplicationType,
    Client,
    CompanyCategoryThresholdViewModel,
    InternalDraftApplicationViewModel,
    User,
    UserType
} from '../../../../../../services/api.client';
import {forkJoin, Observable, of} from 'rxjs';
import {InternalDraftService} from '../../../../../../services/internal-draft.service';
import {PromptComponent} from '../../../../../shared/dialogs/PromptComponent';
import {ActivatedRoute, Router} from '@angular/router';
import {SecurityService} from '../../../../../../services/SecurityService';
import {ClientService} from '../../../../../../services/ClientService';
import {takeUntil} from 'rxjs/operators';
import {AssistanceApplicationFormComponent} from '../assistance-application-form/assistance-application-form.component';
import {Disposable} from '../../../../../../utilities/Disposable';
import {CompanyCategoryService} from '../../../../../../services/companyCategory.service';
import {AssistanceApplicationStatus} from '../../../../../../shared/enums/assistance-application-status';
import {ApplicationTypeRouteMap} from '../../../../../../shared/constants/RouteConstants';
import {FundAdministrativeAgencyNameMap} from '../../../../../../shared/constants/FundConstants';

@Component({
    templateUrl: './create-assistance-application.component.html',
    styleUrls: ['./create-assistance-application.component.scss'],
    standalone: false
})
export class CreateAssistanceApplicationComponent extends Disposable implements OnInit, AfterViewInit, AfterContentChecked {
    administrativeFundName: string;
    applicationType: AssistanceApplicationType;
    client: Client;
    client$: Observable<Client>;

    user: User;
    user$: Observable<User>;

    @ViewChild(AssistanceApplicationFormComponent)
    assistanceApplicationForm: AssistanceApplicationFormComponent;

    @ViewChild(PromptComponent)
    clientInactiveModal: PromptComponent;

    clientCountyInactiveMessage: string;
    draftId: string | null = null;
    draftData: InternalDraftApplicationViewModel | null = null;
    private categoryThresholds: CompanyCategoryThresholdViewModel[];

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private _clientService: ClientService,
        private assistanceApplicationClient: AssistanceApplicationClient,
        private cdref: ChangeDetectorRef,
        private _assistanceApplicationCategoryThresholdService: CompanyCategoryService,
        private internalDraftService: InternalDraftService,
        private _securityService: SecurityService
    ) {
        super();
        // The applicationType route param is the string enum value (e.g. 'UFF'),
        // produced by drafts-table's navigate([... 'add', this.applicationType]).
        // (Under the pre-upgrade numeric enum it was an integer index.)
        const raw = this.activatedRoute.snapshot.paramMap.get('applicationType');

        if (raw === null || AssistanceApplicationType[raw as keyof typeof AssistanceApplicationType] === undefined) {
            this.router.navigate(['app', 'client', this.client?.id]).then();
        }

        this.applicationType = raw as AssistanceApplicationType;
        this.administrativeFundName = FundAdministrativeAgencyNameMap[this.applicationType];
        this.user$ = this._securityService.currentUser$;
        this.client$ = this._clientService.currentClient$;

        this.draftId = this.activatedRoute.snapshot.queryParamMap.get('draftId');
    }

    ngOnInit(): void {
        this.client$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(client => this.client = client);
        this.user$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(user => {
                this.user = user;

                if (!user ||
                    !this.client ||
                    user.userType === UserType.Viewer) {
                    this.router.navigate(
                        ['app', 'client', this.client?.id, 'assistance-applications', ApplicationTypeRouteMap[this.applicationType]]
                    ).then();
                }
            });

        forkJoin([
            this.applicationType === AssistanceApplicationType.UFF ? of(true)
                : this.assistanceApplicationClient.isAssistanceApplicationCountyActive(this.client?.county, this.applicationType),
            this._assistanceApplicationCategoryThresholdService.getCategoryThresholds(this.applicationType)
        ])
            .pipe(takeUntil(this.$destroyed))
            .subscribe({
                next: ([isActive, categoryThresholds]) => {
                    this.categoryThresholds = categoryThresholds;
                    this._assistanceApplicationCategoryThresholdService.setCategoryThresholds(categoryThresholds);
                    if (!isActive) {
                        this.assistanceApplicationForm.form.disable();
                        this.clientCountyInactiveMessage = `The Client County ${this.client?.county} is inactive and is not currently accepting new cases.`;
                        this.clientInactiveModal.open().subscribe(() => this.router.navigate([`app/client/${this.client.id}/efn_cases`]));
                    }
                }
            });
    }

    ngAfterViewInit(): void {

        setTimeout(() => {
            this.assistanceApplicationForm.populateClient(this.client);
            this.assistanceApplicationForm.populateUser(this.user);
            this.assistanceApplicationForm.statusControl.setValue(AssistanceApplicationStatus.UnderReview);
            this.assistanceApplicationForm.statusControl.disable();

            if (this.draftId) {
                this.assistanceApplicationForm.draftId = this.draftId;
                this.internalDraftService.getById(this.draftId).subscribe({
                    next: (draft) => {
                        this.draftData = draft;
                        if (draft?.formData) {
                            this.assistanceApplicationForm.loadFromDraft(draft.formData);
                        }
                        if (draft?.files?.length) {
                            this.assistanceApplicationForm.loadDraftFiles(draft.files);
                        }
                    },
                    error: (err) => {
                        console.error('Failed to load draft application', err);
                    }
                });
            }
        });

    }

    public hasIncome(income: number|undefined|null): boolean {
        return income !== null && income !== undefined && income >= 0;
    }

    async submit(): Promise<void> {
        await this.assistanceApplicationForm.createCase();
    }

    saveDraft(): void {
        this.assistanceApplicationForm.saveDraft();
    }

    ngAfterContentChecked(): void {
        this.cdref.detectChanges();
    }

    protected readonly AssistanceApplicationType = AssistanceApplicationType;
}
